/**
 * Where an email address goes when somebody presses it — ONE copy, for the
 * three surfaces that offer one.
 *
 * Owner's instruction, 2026-09-18: pressing an address opens a draft in
 * **Google's mailbox in the browser**. Every one of these used to be a
 * `mailto:` link, which hands the address to whatever desktop client the
 * machine has registered — Outlook, or nothing at all — so on a browser-only
 * machine the control either opened the wrong application or did nothing
 * visible, which reads as the button being broken rather than as a missing
 * registration.
 *
 * ## ⛔ THREE CALL SITES, AND THAT IS WHY THIS IS A MODULE
 *
 * `CompanyContactsManagerModal` (the contacts window behind the people button
 * on `/companies` and on the University Partnerships cards — it replaced
 * `CompanyContactsMailModal` on 2026-10-16), `CompanyDetailPage`'s
 * `ValueAction` and `ContactDetailPage`'s `ValueAction`. The last two are
 * already near-identical local copies of one another — the `FilterField`
 * species this codebase records — and a third copy of the URL itself would
 * drift on the day one of them was corrected.
 *
 * ⚠️ **What would drift is where a colleague's message is composed**, which is
 * not a rendering detail: an account index pinned in one copy and not another
 * sends two people writing to the same customer from two different mailboxes.
 */

/**
 * A Gmail compose window, addressed to one person.
 *
 * `view=cm` is Gmail's compose view; `fs=1` makes it a full compose rather than
 * the corner panel.
 *
 * ## ⛔ NO `/u/<n>/` ACCOUNT INDEX IS PINNED, and that is the one thing not to "fix"
 *
 * Gmail resolves which account from the browser's own session. Pinning `u/0`
 * sends anybody signed into two accounts to whichever Google happens to order
 * first — so the draft is composed from an address the customer does not
 * recognise, and the person sending it cannot see that from this app.
 *
 * ## ⚠️ "USE THE GMAIL TAB THAT IS ALREADY OPEN" IS NOT BUILDABLE, BY ANYBODY
 *
 * The instruction asked for the draft to open in an already-open Gmail tab
 * where there is one. **No browser API lets a page find, or focus, a tab it did
 * not open itself** — cross-origin, and tab enumeration is not exposed to
 * script at all.
 *
 * A named window target (`target="seed-emerge-gmail"`) would reuse a tab THIS
 * app had opened, and was rejected on two independent grounds. It is only
 * honoured when `rel="noopener"` is dropped, which is the whole job of that
 * attribute; and reusing the tab navigates away from a compose somebody may be
 * part-way through typing, which is a worse failure than an extra tab.
 *
 * What is delivered instead is the substance of it: a person signed into Gmail
 * in that browser lands in Gmail with the draft open, because Gmail's own
 * session does the work the missing API would have done.
 *
 * ## ⛔ `encodeURIComponent`, WHERE THE `mailto:` FORM DELIBERATELY DID NOT
 *
 * *SUPERSEDED, from the dialog this replaces: a partial escape of `\s%?#&`
 * only, so the href went on resembling the label beside it.* That reasoning was
 * about `mailto:`, where the address IS the URL and `@` escaped to `%40` would
 * have disfigured every row to guard against one malformed address. Here the
 * address is a QUERY PARAMETER, so the full escape is both correct and
 * standard, and the href resembles the label in no case whatever.
 *
 * ⚠️ **The hazard that guarded has not gone away** — it is covered by the
 * stricter rule rather than the looser one. A stray `&` in an imported address
 * would otherwise end the `to` parameter and compose a draft addressed to a
 * truncated address, ready to send.
 *
 * ## ⛔ NO SUBJECT, unchanged from the links this replaces
 *
 * A subject composed here is copy nobody approved, written by this app,
 * arriving in a customer's inbox under a colleague's name. The person pressing
 * the button knows what they are writing about; EMERGE does not. Adding one is
 * an owner decision rather than a convenience.
 *
 * ## ⚠️ EVERY CALL SITE MUST OPEN IT IN A NEW TAB
 *
 * This is an ordinary `https:` URL, so — unlike a `mailto:` — following it in
 * place **navigates EMERGE away**. The reader is mid-task on a record or a
 * filtered list; replacing that with Gmail loses their filters, their scroll
 * position and every cached read on the page. Pass `target="_blank"`, and with
 * it `rel="noopener noreferrer"` (which `Button` supplies on its own for
 * `_blank`, and a hand-rolled `<a>` does not).
 */
export function gmailComposeHref(address: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(address)}`;
}

/**
 * 2026-10-16 (owner: "the email address everywhere should have a mailto
 * hyperlink"; chose a real `mailto:` over the Gmail draft). This SUPERSEDES the
 * Gmail rule above for every address shown in the app: use this, or the
 * `EmailLink` primitive (`components/ui/TextLink.tsx`) that wraps it.
 *
 * Followed IN PLACE, never `target="_blank"`: a `mailto:` hands off to the
 * reader's mail app and leaves EMERGE where it was, while a new tab would open
 * an empty page in some browsers.
 *
 * Only the characters that would end or corrupt the address are escaped, so the
 * href still reads as the address (`@` stays `@`). A stray `&`, `?` or `#` in an
 * imported address must not split it into a truncated recipient plus headers.
 * No subject, for the reason given above.
 */
export function mailtoHref(address: string): string {
  return `mailto:${address.trim().replace(/[%?#&\s]/g, (ch) => encodeURIComponent(ch))}`;
}
